import { AllergyIntolerance, Coding } from "@medplum/fhirtypes";
import { ISO_DATE } from "@metriport/shared/common/date";
import { ColDef } from "ag-grid-community";
import dayjs from "dayjs";
import { GenerateTableDataParams } from "..";
import { MrFilterSetting } from "../../../../../api/settings";
import { EhrName } from "../../../../../domain/ehr";
import {
  compare,
  createEhrActionColumnDef,
  filterByDate,
  getFirstCodeSpecified,
  getResourcesFromBundle,
  RX_NORM_CODE,
  SNOMED_CODE,
} from "../shared";

export type AllergyRowData = {
  id: string;
  allergy: string;
  manifestation: string;
  firstSeen: string;
  status: string;
  originalData: AllergyIntolerance;
  ehrAction?: string;
};

export const allergyTableData = ({ bundle, tableFilters, ehrActions }: GenerateTableDataParams) => {
  const columnDefs: ColDef<AllergyRowData>[] = [
    { field: "id", hide: true },
    { field: "allergy" },
    { field: "manifestation" },
    { field: "firstSeen", sort: tableFilters?.stringFilter ? undefined : "desc" },
    { field: "status" },
    { field: "originalData", hide: true },
  ];

  if (
    ehrActions &&
    ehrActions.write &&
    (ehrActions.name === EhrName.Athena ||
      ehrActions.name === EhrName.Canvas ||
      ehrActions.name === EhrName.Healthie)
  ) {
    const columnDef = createEhrActionColumnDef<AllergyIntolerance, AllergyRowData>({
      ehrActions,
      path: "allergy",
      filter: allergyIntolerance => isShowWriteBackButton(ehrActions.name, allergyIntolerance),
    });
    columnDefs.push(columnDef);
  }

  const allergies = getResourcesFromBundle<AllergyIntolerance>(bundle, "AllergyIntolerance");

  return {
    columnDefs,
    rowData: getAllergyRowData({ allergies, tableFilters }),
  };
};

function getAllergyRowData({
  allergies,
  tableFilters,
}: {
  allergies: AllergyIntolerance[];
  tableFilters: MrFilterSetting | undefined;
}): AllergyRowData[] {
  return allergies
    ?.map(allergy => ({
      id: allergy.id ?? "-",
      allergy: getAllergyName(allergy),
      manifestation: getAllergyManifestation(allergy),
      firstSeen: getAllergyOnset(allergy),
      status: getAllergyClinicalStatus(allergy),
      originalData: allergy,
    }))
    .filter(row => filterByDate(row.firstSeen, tableFilters?.dateFilter))
    .sort((a, b) =>
      compare(
        { ...a, originalData: undefined, id: undefined },
        { ...b, originalData: undefined, id: undefined },
        tableFilters?.stringFilter
      )
    );
}

function getAllergyName(allergy: AllergyIntolerance): string {
  const names: string[] = [];

  for (const reaction of allergy.reaction ?? []) {
    if (reaction.substance?.text) {
      names.push(reaction.substance.text);
    }
  }

  if (names && names.length > 0) {
    return names.join(", ");
  }

  return "-";
}

function getAllergyManifestation(allergy: AllergyIntolerance): string {
  const manifestations: string[] = [];

  for (const reaction of allergy.reaction ?? []) {
    for (const manifestation of reaction.manifestation ?? []) {
      if (manifestation.text) {
        manifestations.push(manifestation.text);
      }
    }
  }

  if (manifestations && manifestations.length > 0) {
    return manifestations.join(", ");
  }

  return "-";
}

function getAllergyOnset(allergy: AllergyIntolerance): string {
  if (allergy.onsetDateTime) {
    return dayjs(allergy.onsetDateTime).format(ISO_DATE);
  } else if (allergy.recordedDate) {
    return dayjs(allergy.recordedDate).format(ISO_DATE);
  }

  return "-";
}

function getAllergyClinicalStatus(allergy: AllergyIntolerance): string {
  return allergy.clinicalStatus?.coding?.[0]?.code ?? "-";
}

function isShowWriteBackButton(ehr: EhrName, allergyIntolerance: AllergyIntolerance): boolean {
  const reaction = allergyIntolerance.reaction;
  if (!reaction || reaction.length < 1) return false;
  const codingsPairs: [Coding, Coding | undefined][] = reaction.flatMap(r => {
    const substanceRxnormCoding = getFirstCodeSpecified(r.substance?.coding ?? [], [RX_NORM_CODE]);
    if (!substanceRxnormCoding) return [];
    const manifestationSnomedCoding = getFirstCodeSpecified(
      r.manifestation?.flatMap(m => m.coding ?? []),
      [SNOMED_CODE]
    );
    if (ehr === EhrName.Athena && !manifestationSnomedCoding) return [];
    return [[substanceRxnormCoding, manifestationSnomedCoding]];
  });
  if (codingsPairs.length < 1) return false;
  if (ehr === EhrName.Healthie) return true;
  const onset = allergyIntolerance.onsetDateTime ?? allergyIntolerance.onsetPeriod?.start;
  if (!onset) return false;
  if (ehr === EhrName.Canvas) {
    const clinicalStatus = allergyIntolerance.clinicalStatus;
    if (!clinicalStatus) return false;
    const verificationStatus = allergyIntolerance.verificationStatus;
    if (!verificationStatus) return false;
  }
  return true;
}
